import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { api, type ApiResponse } from "~/lib/api";
import { ak, PageHead, Section, Status, Tag } from "~/components/editorial";
import type { Tool, Label } from "./types";
import { AUTH_METHOD_LABELS } from "./types";

function getRenewalStatus(
  renewalDate: string | null,
): "overdue" | "soon" | "ok" | null {
  if (!renewalDate) return null;
  const d = new Date(renewalDate);
  const now = new Date();
  if (d < now) return "overdue";
  const in30 = new Date();
  in30.setDate(in30.getDate() + 30);
  if (d <= in30) return "soon";
  return "ok";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ToolsPage() {
  const navigate = useNavigate();
  const [labelId, setLabelId] = useState("");
  const [renewal, setRenewal] = useState("");
  const [jiraProjectId, setJiraProjectId] = useState("");
  const [viewMode, setViewMode] = useState<"card" | "table">("card");

  const { data: labelsData } = useQuery({
    queryKey: ["labels"],
    queryFn: () => api.get<ApiResponse<Label[]>>("/labels"),
  });
  const labels = labelsData?.data ?? [];

  const params = new URLSearchParams();
  if (labelId) params.set("labelId", labelId);
  if (renewal) params.set("renewal", renewal);
  const queryString = params.toString();

  const { data, isLoading } = useQuery({
    queryKey: ["tools", labelId, renewal],
    queryFn: () =>
      api.get<ApiResponse<Tool[]>>(
        `/tools${queryString ? `?${queryString}` : ""}`,
      ),
  });
  const tools = data?.data ?? [];

  const jiraProjects = useMemo(() => {
    const seen = new Set<string>();
    const result: { id: string; name: string }[] = [];
    for (const tool of tools) {
      for (const jt of tool.jiraTeams) {
        if (!seen.has(jt.jiraProject.id)) {
          seen.add(jt.jiraProject.id);
          result.push({ id: jt.jiraProject.id, name: jt.jiraProject.name });
        }
      }
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [tools]);

  const filteredTools = jiraProjectId
    ? tools.filter((t) =>
        t.jiraTeams.some((jt) => jt.jiraProject.id === jiraProjectId),
      )
    : tools;

  const overdue = filteredTools.filter(
    (t) => getRenewalStatus(t.renewalDate) === "overdue",
  ).length;
  const soon = filteredTools.filter(
    (t) => getRenewalStatus(t.renewalDate) === "soon",
  ).length;
  const paid = filteredTools.filter((t) => t.isPaid).length;
  const filtered = !!(labelId || renewal || jiraProjectId);

  if (isLoading) {
    return (
      <div className={ak.page}>
        <div className={ak.loading}>Loading tools…</div>
      </div>
    );
  }

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin · Tools tracker"
        title={toolsHeadline(filteredTools.length, overdue, soon, filtered)}
        dek={
          filteredTools.length > 0 ? (
            <>
              <span className={ak.mono}>{paid}</span> of{" "}
              <span className={ak.mono}>{filteredTools.length}</span> are paid.
              Each entry records how people get access, who should have it and
              which teams use it.
            </>
          ) : (
            "Track how people get access to each tool, who should have it, which teams use it and when it renews."
          )
        }
        actions={
          <>
            <button
              onClick={() => navigate("/labels")}
              className={ak.btnSecondary}
            >
              Manage labels
            </button>
            <button onClick={() => navigate("/tools/new")} className={ak.btn}>
              Add tool
            </button>
          </>
        }
      />

      <Section>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <label className={styles.filter}>
              <span className={ak.label}>Team</span>
              <select
                value={jiraProjectId}
                onChange={(e) => setJiraProjectId(e.target.value)}
                className={cx(ak.select, ak.selectSm)}
              >
                <option value="">All teams</option>
                {jiraProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filter}>
              <span className={ak.label}>Label</span>
              <select
                value={labelId}
                onChange={(e) => setLabelId(e.target.value)}
                className={cx(ak.select, ak.selectSm)}
              >
                <option value="">All labels</option>
                {labels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filter}>
              <span className={ak.label}>Renewal</span>
              <select
                value={renewal}
                onChange={(e) => setRenewal(e.target.value)}
                className={cx(ak.select, ak.selectSm)}
              >
                <option value="">All renewals</option>
                <option value="upcoming">Next 30 days</option>
                <option value="overdue">Overdue</option>
              </select>
            </label>
          </div>
          <div className={ak.tabs} role="tablist" aria-label="View">
            <button
              role="tab"
              aria-selected={viewMode === "card"}
              className={ak.tab}
              onClick={() => setViewMode("card")}
            >
              Cards
            </button>
            <button
              role="tab"
              aria-selected={viewMode === "table"}
              className={ak.tab}
              onClick={() => setViewMode("table")}
            >
              Table
            </button>
          </div>
        </div>

        {filteredTools.length === 0 ? (
          <div className={ak.empty}>
            <p>
              {filtered ? "No tools match these filters." : "No tools yet."}
            </p>
            <p>
              {filtered ? (
                "Clear a filter to see more."
              ) : (
                <button
                  onClick={() => navigate("/tools/new")}
                  className={ak.btnText}
                >
                  Add the first tool
                </button>
              )}
            </p>
          </div>
        ) : viewMode === "card" ? (
          <div className={styles.grid}>
            {filteredTools.map((tool) => {
              const renewalStatus = getRenewalStatus(tool.renewalDate);
              return (
                <Link
                  key={tool.id}
                  to={`/tools/${tool.id}`}
                  className={styles.card}
                >
                  <div className={styles.cardHead}>
                    <h3 className={styles.name}>{tool.name}</h3>
                    {tool.authMethod && (
                      <span className={styles.auth}>
                        {AUTH_METHOD_LABELS[tool.authMethod] ?? tool.authMethod}
                      </span>
                    )}
                  </div>

                  {tool.description && (
                    <p className={styles.description}>{tool.description}</p>
                  )}

                  {tool.labels.length > 0 && (
                    <div className={ak.tagRow}>
                      {tool.labels.map(({ label }) => (
                        <Tag
                          key={label.id}
                          name={label.name}
                          color={label.color}
                        />
                      ))}
                    </div>
                  )}

                  <div className={styles.cardFoot}>
                    <span>
                      <span className={ak.mono}>{tool.jiraTeams.length}</span>{" "}
                      team{tool.jiraTeams.length !== 1 ? "s" : ""}
                    </span>
                    <RenewalCell
                      status={renewalStatus}
                      date={tool.renewalDate}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className={ak.tableWrap}>
            <table className={ak.table}>
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>Auth</th>
                  <th>Labels</th>
                  <th>Teams</th>
                  <th data-num>Renews</th>
                  <th>Renewal</th>
                </tr>
              </thead>
              <tbody>
                {filteredTools.map((tool) => {
                  const renewalStatus = getRenewalStatus(tool.renewalDate);
                  return (
                    <tr key={tool.id}>
                      <td>
                        <Link
                          to={`/tools/${tool.id}`}
                          className={styles.tableName}
                        >
                          {tool.name}
                        </Link>
                      </td>
                      <td className={styles.tableMuted}>
                        {tool.authMethod
                          ? AUTH_METHOD_LABELS[tool.authMethod] ??
                            tool.authMethod
                          : "–"}
                      </td>
                      <td>
                        <div className={ak.tagRow}>
                          {tool.labels.map(({ label }) => (
                            <Tag
                              key={label.id}
                              name={label.name}
                              color={label.color}
                            />
                          ))}
                        </div>
                      </td>
                      <td className={styles.tableMuted}>
                        {tool.jiraTeams
                          .map((jt) => jt.jiraProject.name)
                          .join(", ") || "–"}
                      </td>
                      <td data-num>
                        {tool.renewalDate ? formatDate(tool.renewalDate) : "–"}
                      </td>
                      <td>
                        {renewalStatus === "overdue" && (
                          <Status kind="fail">Overdue</Status>
                        )}
                        {renewalStatus === "soon" && (
                          <Status kind="wait">Within 30 days</Status>
                        )}
                        {renewalStatus === "ok" && (
                          <Status kind="ok">Current</Status>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className={ak.note}>
          Renewal status compares the renewal date with today: ▲ overdue, ◇
          renews within 30 days, ● current. Teams come from linked Jira
          projects. Label swatches keep the color picked for each label.
        </p>
      </Section>
    </div>
  );
}

function RenewalCell({
  status,
  date,
}: {
  status: ReturnType<typeof getRenewalStatus>;
  date: string | null;
}) {
  if (!date || !status) return <span className={ak.muted}>No renewal</span>;
  const kind =
    status === "overdue" ? "fail" : status === "soon" ? "wait" : "ok";
  const label =
    status === "overdue" ? "Overdue" : status === "soon" ? "Renews" : "Renews";
  return (
    <Status kind={kind}>
      {label} <span className={ak.mono}>{formatDate(date)}</span>
    </Status>
  );
}

function toolsHeadline(
  total: number,
  overdue: number,
  soon: number,
  filtered: boolean,
): string {
  if (total === 0) return filtered ? "No tools match" : "Tools tracker";
  const noun = total === 1 ? "tool" : "tools";
  const head = `${total} ${noun}${filtered ? " in view" : " tracked"}`;
  if (overdue > 0 && soon > 0) {
    return `${head}; ${overdue} overdue for renewal and ${soon} due within 30 days`;
  }
  if (overdue > 0) return `${head}; ${overdue} overdue for renewal`;
  if (soon > 0) return `${head}; ${soon} renew within 30 days`;
  return `${head}, none due for renewal this month`;
}

const styles = {
  toolbar: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "12px 24px",
    mb: "14px",
  }),
  filters: css({ display: "flex", flexWrap: "wrap", gap: "12px" }),
  filter: css({ display: "flex", flexDirection: "column", gap: "4px" }),
  grid: css({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderLeftWidth: "1px",
    borderLeftStyle: "solid",
    borderColor: "ed.rule",
  }),
  card: css({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    p: "14px",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
    color: "ed.ink",
    textDecoration: "none",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.panel" },
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "ed.ink",
      outlineOffset: "-2px",
    },
  }),
  cardHead: css({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px",
  }),
  name: css({
    fontFamily: "serif",
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: 1.2,
    m: 0,
  }),
  auth: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink3",
    whiteSpace: "nowrap",
  }),
  description: css({
    fontSize: "13px",
    lineHeight: 1.45,
    color: "ed.ink2",
    m: 0,
    lineClamp: 2,
  }),
  cardFoot: css({
    mt: "auto",
    pt: "8px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: "4px 12px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.grid",
    fontSize: "12px",
    color: "ed.ink2",
  }),
  tableName: css({
    fontFamily: "serif",
    fontSize: "15px",
    fontWeight: 700,
    color: "ed.ink",
    textDecoration: "none",
    _hover: { textDecoration: "underline" },
  }),
  tableMuted: css({ color: "ed.ink2", fontSize: "12.5px" }),
};
